import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { useDispatch, useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers';
import { Box, Card, Grid, Stack, Container, CardHeader, CardContent, TextField } from '@mui/material';
import { getAuditUsers } from '../../users/store/userActions';
import { getAuditDepartments } from '../../departments/store/departmentActions';
import { getAuditBranches } from '../../branches/store/branchActions';
import { FormProvider, RHFSelect, RHFTextField } from '../../../components/hook-form';
import { Page } from '../../../components';
import { getAssetDetail, updateAsset } from '../store/assetActions';
import { fDateYMD } from '../../../utils/formatTime';

const ASSET_STATUS = [
  {
    id: 1,
    label: 'Active',
    value: 'active',
  },
  {
    id: 2,
    label: 'In Store',
    value: 'in_store',
  },
  {
    id: 3,
    label: 'Retired',
    value: 'retired',
  },
  {
    id: 4,
    label: 'Disposed',
    value: 'disposed',
  },
];

NewAssetForm.propTypes = {
  isEdit: PropTypes.bool,
  fixedAsset: PropTypes.object,
  onSuccess: PropTypes.func,
  inModal: PropTypes.bool,
};

export default function NewAssetForm({ isEdit, fixedAsset, onSuccess, inModal = false }) {
  const dispatch = useDispatch();
  const { auditBranches } = useSelector((state) => state.branchReducer);
  const { auditUsers } = useSelector((state) => state.userReducer);
  const { auditDepartments } = useSelector((state) => state.departmentReducer);

  console.log('USERS:', auditUsers);

  const [assignedDate, setAssignedDate] = useState(
    fixedAsset?.assigned_date ? new Date(fixedAsset.assigned_date) : new Date()
  );

  const { enqueueSnackbar } = useSnackbar();
  const NewAssetSchema = Yup.object().shape({
    hostname: Yup.string(),
    ramSize: Yup.number(),
    diskSize: Yup.number(),
    processor: Yup.string(),
    model: Yup.string(),
    location: Yup.string(),
    custodian: Yup.string(),
    macAddress: Yup.string(),
    department: Yup.string(),
  });

  const defaultValues = {
    hostname: fixedAsset?.hostname || '',
    ramSize: fixedAsset?.ram_size || 0,
    diskSize: fixedAsset?.disk_size || 0,
    processor: fixedAsset?.processor || '',
    model: fixedAsset?.model || '',
    macAddress: fixedAsset?.mac_address || '',
    custodian: fixedAsset?.custodian?.id || '',
    location: fixedAsset?.location?.id || '',
    status: fixedAsset?.status || '',
    department: fixedAsset?.department || '',
  };

  const methods = useForm({
    resolver: yupResolver(NewAssetSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    dispatch(getAuditBranches({}));
    dispatch(getAuditUsers({}));
    dispatch(getAuditDepartments());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (fixedAsset) {
      setAssignedDate(fixedAsset.assigned_date ? new Date(fixedAsset.assigned_date) : new Date());
      reset({
        hostname: fixedAsset?.hostname || '',
        ramSize: fixedAsset?.ram_size || 0,
        diskSize: fixedAsset?.disk_size || 0,
        processor: fixedAsset?.processor || '',
        model: fixedAsset?.model || '',
        macAddress: fixedAsset?.mac_address || '',
        custodian: fixedAsset?.custodian?.id || '',
        location: fixedAsset?.location?.id || '',
        status: fixedAsset?.status || '',
        department: fixedAsset?.department || '',
      });
    }
  }, [fixedAsset, reset]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (data) => {
    const payload = {
      hostname: data.hostname,
      ram_size: data.ramSize,
      disk_size: data.diskSize,
      processor: data.processor,
      model: data.model,
      assigned_date: fDateYMD(assignedDate),
      mac_address: data.macAddress,
      location_id: data.location,
      custodian_id: data.custodian,
      status: data.status,
      department: data.department,
    };
    console.log('PAYLOAD:', payload);
    try {
      if (!fixedAsset?.id) {
        return;
      }
      await dispatch(updateAsset({ id: fixedAsset.id, payload }));

      reset();
      enqueueSnackbar('Update success!');
      if (onSuccess) {
        onSuccess();
      } else {
        dispatch(getAssetDetail(fixedAsset.id));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const formContent = (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12} lg={12}>
          <Card sx={{ p: inModal ? 0 : 3, boxShadow: inModal ? 'none' : undefined }}>
            {!inModal && <CardHeader title={isEdit ? 'Edit Asset' : 'Create Asset'} subheader={''} />}
            <CardContent sx={{ p: inModal ? 2 : undefined }}>
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                }}
              >
                <RHFTextField name="hostname" label="Hostname" />
                <RHFTextField type="number" name="ramSize" label="RamSize" />
                <RHFTextField type="number" name="diskSize" label="Disk Size" />
                <RHFTextField name="processor" label="Processor" />
                <RHFTextField name="model" label="Model" />
                <RHFTextField
                  name="macAddress"
                  label="Mac Address"
                  inputProps={{
                    pattern: '^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$',
                    title: 'Please enter a valid MAC address format (e.g., AA:BB:CC:DD:EE:FF or AA-BB-CC-DD-EE-FF)',
                  }}
                />
                <RHFSelect name="custodian" label="Custodian" placeholder="Custodian">
                  <option value="" />
                  {auditUsers?.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.display_name}
                    </option>
                  ))}
                </RHFSelect>

                <DatePicker
                  label={
                    <span>
                      Assigned Date<span style={{ color: 'red' }}> *</span>
                    </span>
                  }
                  value={assignedDate}
                  onChange={(newValue) => setAssignedDate(newValue)}
                  maxDate={new Date()}
                  renderInput={(params) => <TextField {...params} />}
                />
                <RHFSelect name="location" label="Location" placeholder="Location">
                  <option value="" />
                  {auditBranches?.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </RHFSelect>
                <RHFSelect name="status" label="Status" placeholder="Asset Status">
                  <option value="" />
                  {ASSET_STATUS?.map((option) => (
                    <option key={option.id} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </RHFSelect>
                <RHFSelect name="department" label="Department" placeholder="Department">
                  <option value="" />
                  {auditDepartments?.map((dept) => (
                    <option key={dept.id} value={dept.name}>
                      {dept.name}
                    </option>
                  ))}
                </RHFSelect>
              </Box>

              <Stack alignItems="flex-end" sx={{ mt: 3 }}>
                <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                  Save Changes
                </LoadingButton>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );

  if (inModal) {
    return formContent;
  }

  return (
    <Page title="New Asset">
      <Container maxWidth="xl">{formContent}</Container>
    </Page>
  );
}
